import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
} from "features/layout/components";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "common/components/ui/Breadcrumb";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLeadList from "../../datahooks/useLeadList";
import useLeadListSources from "../../datahooks/useLeadListSources";
import AddLeadsButton from "./AddLeadsButton";
import LeadListHeading from "./LeadListHeading";
import LeadListLeads from "./LeadListLeads";
import LeadListSources from "./LeadListSources";

import NoSourcesPlaceholderImage from "assets/images/empty-placeholders/lead-list-no-sources.png";

export default function LeadListDetails() {
  const isDesktop = useTwBreakpoint("lg");

  const { listId } = useParams();
  const { state } = useLocation();
  const { leadList } = useLeadList({
    listId,
    placeholderList: state?.leadList,
  });
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  const {
    leadListSources,
    isLoadingLeadListSources,
    leadListSourcesError,
    refetchLeadListSources,
  } = useLeadListSources({
    listId,
  });

  const hasSources = !!leadListSources?.length;

  const addLeadsButton = <AddLeadsButton listId={listId} />;

  let mainComponent = (
    <EmptyPlaceholder
      title="No leads yet"
      subtitle="Create your first source to start collecting LinkedIn leads"
      imageSrc={NoSourcesPlaceholderImage}
      actionComponent={addLeadsButton}
    />
  );

  if (leadListSourcesError) {
    mainComponent = (
      <ErrorPlaceholder
        errorMessage="Error loading list sources"
        onRetry={refetchLeadListSources}
      />
    );
  } else if (hasSources || isLoadingLeadListSources) {
    mainComponent = (
      <div className="flex-col flex gap-y-4 grow">
        <RenderIf condition={!isDesktop}>
          <div className="flex flex-row gap-2 justify-between items-center">
            {hasSources && addLeadsButton}

            {/* TODO: Add export leads button later on */}
          </div>
        </RenderIf>

        <LeadListSources
          listId={listId}
          selectedSourceId={selectedSourceId}
          selectSource={(sourceId) => setSelectedSourceId(sourceId)}
        />

        <LeadListLeads listId={listId} />
      </div>
    );
  }

  return (
    <ContentContainer>
      <RenderIf condition={isDesktop}>
        <ContentHeader>
          <Breadcrumb className="overflow-hidden">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="..">Lead Lists</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbSeparator />

              <BreadcrumbItem>
                <BreadcrumbPage>Details</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {!isLoadingLeadListSources && hasSources && addLeadsButton}
        </ContentHeader>
      </RenderIf>

      <ContentScroll className="flex flex-col gap-4 p-4 max-lg:pt-6">
        <LeadListHeading leadList={leadList} />
        {mainComponent}
      </ContentScroll>
    </ContentContainer>
  );
}
